import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { uuidv7 as uuid } from "uuidv7";

import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { AddToDropdown } from "src/app/components/AddToDropdown/AddToDropdown";
import { ReactComponent as RocketIcon } from "src/images/rocket-grey.svg";
import AddCreatorsCDTWorkspaceProjectModal from "src/app/modals/AddCreatorsCDTWorkspaceProjectModal/AddCreatorsCDTWorkspaceProjectModal";
import { setCreateNewProjectData } from "src/redux/main/mainActions";
import { navigateToCreateNewProject } from "src/app/SettingsModal/methods";
import { showToast } from "src/app/methods/showToast";
import responseCodes from "src/utils/responseCodes";
import { ReactComponent as CreatorIcon } from "src/images/creator-discovery.svg";
import { createGlobalTasks } from "src/redux/creator-database/creatorDatabaseActions";
import { DropdownMenuItem } from "src/app/components/Dropdown/Dropdown";
import {
  generateSelectedInfluencersWithSocialProfiles,
  selectedCreatorsPayload,
  generateSocialProfiles,
} from "./utils";

export default function AddToDropdownOptions({
  activeTab,
  influencersInList,
  searchAudienceResult,
  selectedInfluencersUrls,
  selectedInfluencers,
  children,
}) {
  const [
    showModalAddCreatorsToWorkspaceProject,
    setShowModalAddCreatorsToWorkspaceProject,
  ] = useState(false);

  const activeWorkspaceUuid = useSelector(
    (state) => state.mainReducer.activeWorkspaceUuid,
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const dispatchDataAndNavigateToCreateNewProject = () => {
    const records =
      activeTab === "selected" ? influencersInList : searchAudienceResult;

    dispatch(
      setCreateNewProjectData({
        selectedCreatorsPayload: selectedCreatorsPayload(
          selectedInfluencersUrls,
          records,
        ),
      }),
    );
    navigateToCreateNewProject(history, activeWorkspaceUuid);
  };

  const addCreatorsToDatabase = () => {
    dispatch(
      createGlobalTasks(
        activeWorkspaceUuid,
        "creator",
        selectedNonGlobalInfluencers,
      ),
    ).then((res) => {
      if (res?.payload?.status === responseCodes["200_OK"]) {
        showToast(
          "success",
          <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
          <IDHFormattedMessage
            id="ws_creators_added_to_database"
            defaultMessage="Creators successfully added to Database"
          />,
        );
      }
    });
  };

  const selectedNonGlobalInfluencers = useMemo(() => {
    if (!selectedInfluencers) return [];

    const list =
      activeTab === "selected" ? influencersInList : searchAudienceResult;

    const influList = [];
    selectedInfluencers.forEach((influ) => {
      const influData = list?.find((item) => item.userId === influ);
      if (influData && !influData.wsGlobalTaskUuid) {
        const { socialProviders } = influData;
        const globalTaskData = {
          uuid: uuid(),
          name: influData.socialUsername,
          displayName: influData?.name ?? null,
          socialProfiles: generateSocialProfiles(socialProviders),
        };
        influList.push(globalTaskData);
      }
    });
    return influList;
  }, [activeTab, selectedInfluencers, influencersInList, searchAudienceResult]);

  const addCreatorsToProjectDisabled =
    selectedInfluencersUrls?.length < 1 ||
    selectedInfluencersUrls?.length > 100;

  return (
    <>
      <AddToDropdown>
        <MaterialTooltip
          center
          content={
            selectedInfluencersUrls?.length > 100 ? (
              <IDHFormattedMessage
                id="front_you_can_add_only_100_influencers"
                defaultMessage="You can add only 100 influencers to campaign at once"
              />
            ) : (
              <IDHFormattedMessage
                id="front_select_creator_to_add_to_workspace_campaign"
                defaultMessage="Select at least one Creator to add to the Campaign"
              />
            )
          }
          contentHidden={!addCreatorsToProjectDisabled}
        >
          <DropdownMenuItem
            onClick={() => setShowModalAddCreatorsToWorkspaceProject(true)}
            disabled={addCreatorsToProjectDisabled}
            data-qa="add-to-campaign-dropdown-menu-item"
          >
            <RocketIcon />
            <IDHFormattedMessage
              id="front_campaign"
              defaultMessage="Campaign"
            />
          </DropdownMenuItem>
        </MaterialTooltip>
        <MaterialTooltip
          center
          content={
            <IDHFormattedMessage
              id="front_select_non_global_creator_to_add_to_database"
              defaultMessage="Select at least one local Creator to add to the Creator Database"
            />
          }
          contentHidden={selectedNonGlobalInfluencers.length}
        >
          <DropdownMenuItem
            onClick={addCreatorsToDatabase}
            disabled={!selectedNonGlobalInfluencers.length}
            data-qa="add-to-creator-database"
          >
            <CreatorIcon />
            <IDHFormattedMessage
              id="front_creator_database"
              defaultMessage="Creator Database"
            />
          </DropdownMenuItem>
        </MaterialTooltip>
        {children}
      </AddToDropdown>
      {showModalAddCreatorsToWorkspaceProject && (
        <AddCreatorsCDTWorkspaceProjectModal
          onClose={() => setShowModalAddCreatorsToWorkspaceProject(false)}
          activeWorkspaceUuid={activeWorkspaceUuid}
          selectedCreators={generateSelectedInfluencersWithSocialProfiles(
            selectedInfluencersUrls,
            searchAudienceResult,
          )}
          openCreateNewProjectModal={dispatchDataAndNavigateToCreateNewProject}
        />
      )}
    </>
  );
}
